import React, { FC, useMemo } from 'react';
import { BasicContainer, FlexCol, FlexDynamic, TextDynamic } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { appConfig } from '@minecraft.app-config';

export type BrandName = 'hulu' | 'netflix' | 'prime' | 'hbo' | 'visa' | 'nike' | 'verizon' | 'amazon' | 'toyota';

export const ALL_BRANDS: BrandName[] = [
  'hulu',
  'netflix',
  'prime',
  'hbo',
  'visa',
  'nike',
  'verizon',
  'amazon',
  'toyota',
];

interface BrandInfo {
  id: BrandName;
  name: string;
  transparentImg: string;
  colorImg: string;
}

const TRUSTED_BRANDS: BrandInfo[] = [
  {
    id: 'hulu' as const,
    name: 'Hulu',
    transparentImg: 'bw-hulu.png',
    colorImg: 'hulu.png',
  },
  {
    id: 'netflix' as const,
    name: 'Netflix',
    transparentImg: 'bw-netflix.png',
    colorImg: 'netflix.png',
  },
  {
    id: 'prime' as const,
    name: 'Prime',
    transparentImg: 'bw-prime.png',
    colorImg: 'prime.png',
  },
  {
    id: 'hbo' as const,
    name: 'HBO',
    transparentImg: 'bw-max.png',
    colorImg: 'max.png',
  },
  {
    id: 'visa' as const,
    name: 'Visa',
    transparentImg: 'bw-visa.png',
    colorImg: 'visa.png',
  },
  {
    id: 'nike' as const,
    name: 'Nike',
    transparentImg: 'bw-nike.png',
    colorImg: 'nike.png',
  },
  {
    id: 'verizon' as const,
    name: 'Verizon',
    transparentImg: 'bw-verizon.png',
    colorImg: 'verizon.png',
  },
  {
    id: 'amazon' as const,
    name: 'Amazon',
    transparentImg: 'bw-amazon.png',
    colorImg: 'amazon.png',
  },
  {
    id: 'toyota' as const,
    name: 'Toyota',
    transparentImg: 'bw-toyota-v2.png',
    colorImg: 'toyota-v2.png',
  },
];

export type TrustedBrandsProps = {
  align?: 'left' | 'center';
  isTransparent?: boolean;
  showBrands?: BrandName[];
};

export const TrustedBrands: FC<TrustedBrandsProps> = React.memo(
  ({ isTransparent, align = 'center', showBrands = ALL_BRANDS }) => {
    const { t } = useTranslation();

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      img.style.display = 'none'; // Hide broken images
      img.onerror = null; // Prevent potential memory leaks
    };

    const brandsToShow = useMemo(() => {
      if (!Array.isArray(showBrands) || showBrands.length === 0) {
        return TRUSTED_BRANDS;
      }

      return TRUSTED_BRANDS.filter((tb) => showBrands.includes(tb.id));
    }, [showBrands]);

    return (
      <FlexCol
        w="full"
        alignItems={align === 'center' ? 'center' : 'flex-start'}
        gap="4"
        data-testid="trusted-by-brands-container"
      >
        <TextDynamic
          align={align === 'center' ? 'center' : 'left'}
          fontWeight="semibold"
          color={isTransparent ? 'lowContrastText' : 'disabled'}
          as="h3"
          fontSize="bodyLarge"
        >
          {t('common:paywall.checkout.trustedBrands')}
        </TextDynamic>
        <FlexDynamic
          w="full"
          justifyContent={align === 'center' ? 'center' : 'flex-start'}
          alignItems="center"
          gap="6"
          className="cn_atom_gap-y-4"
          data-testid="trusted-brands-logos-wrap"
          mdProps={{ gap: '8' }}
        >
          {brandsToShow.map((brand) => (
            <BasicContainer key={brand.id}>
              <img
                src={`${appConfig.cdnRoot}/images/static/${isTransparent ? brand.transparentImg : brand.colorImg}`}
                alt={brand.name}
                className="cn_object-contain"
                onError={handleImageError}
              />
            </BasicContainer>
          ))}
        </FlexDynamic>
      </FlexCol>
    );
  }
);
